/** Редактор Components V2: контейнеры, текст, секции, галереи, файлы, разделители, ряды. */
import {
  cloneWithNewIds,
  newButton,
  newContainer,
  newFile,
  newGallery,
  newMediaItem,
  newRow,
  newSection,
  newSeparator,
  newText,
} from '../../../src/shared/factory.js';
import { LIMITS } from '../../../src/shared/limits.js';
import { countV2Components, v2TextLength } from '../../../src/shared/validate.js';
import type { DContainer, DSection, DV2Child, DV2Top } from '../../../src/shared/types.js';
import { h, hexColor, icon } from '../dom.js';
import { store } from '../store.js';
import { colorField, mediaField, pickFile, selectField, switchField, textField, uploadFile } from '../ui/inputs.js';
import { toast } from '../ui/overlay.js';
import { addButton, emptyHint, group, itemCard, listActions, segmented, sectionPanel, type AddItem } from './common.js';
import { buttonFields, rowEditor } from './components.js';

const NAMES: Record<string, { title: string; icon: string }> = {
  container: { title: 'Контейнер', icon: 'box' },
  text: { title: 'Текст', icon: 'text' },
  section: { title: 'Секция', icon: 'section' },
  gallery: { title: 'Галерея', icon: 'image' },
  file: { title: 'Файл', icon: 'file' },
  separator: { title: 'Разделитель', icon: 'separator' },
  row: { title: 'Ряд кнопок', icon: 'mouse' },
};

function addItems(push: (c: DV2Child | DV2Top) => void, allowContainer: boolean): AddItem[] {
  const items: AddItem[] = [];
  if (allowContainer) items.push({ icon: 'box', label: 'Контейнер', desc: 'Карточка с цветной полосой, как эмбед', onClick: () => push(newContainer()) });
  items.push(
    { icon: 'text', label: 'Текст', desc: 'Markdown, заголовки, списки', onClick: () => push(newText('Новый текст')) },
    { icon: 'section', label: 'Секция', desc: 'Текст + миниатюра или кнопка справа', onClick: () => push(newSection()) },
    { icon: 'image', label: 'Галерея', desc: 'От 1 до 10 картинок или видео', onClick: () => push(newGallery()) },
    { icon: 'file', label: 'Файл', desc: 'Вложение из раздела «Вложения»', onClick: () => push(newFile(store.draft.attachments[0]?.name ?? '')) },
    { icon: 'separator', label: 'Разделитель', desc: 'Отступ и линия', onClick: () => push(newSeparator()) },
    { icon: 'mouse', label: 'Ряд кнопок', desc: 'До 5 кнопок или меню выбора', onClick: () => push(newRow()) },
  );
  return items;
}

function sectionFields(s: DSection): HTMLElement[] {
  const acc = s.accessory;
  const accBox = h('div');
  const renderAcc = () => {
    accBox.replaceChildren();
    const a = s.accessory;
    if (a.type === 'thumbnail') {
      accBox.append(
        mediaField('Миниатюра', a.url, (v) => store.input(`${s.id}.thumb`, () => (a.url = v)), { hint: 'Картинка справа от текста (квадрат 85×85)' }),
        textField({ label: 'Описание (alt)', value: a.description, max: LIMITS.mediaDescription, onInput: (v) => store.input(`${s.id}.thumbd`, () => (a.description = v)) }),
        switchField('Спойлер', a.spoiler, (v) => store.input(`${s.id}.thumbs`, () => (a.spoiler = v))),
      );
    } else {
      accBox.append(...buttonFields(a));
    }
  };
  renderAcc();
  return [
    group(
      `Текст (${s.texts.length}/${LIMITS.sectionTexts})`,
      ...s.texts.map((t, i) =>
        h(
          'div',
          { class: 'section-text', dataset: { id: t.id } },
          textField({
            label: `Блок ${i + 1}`,
            value: t.content,
            multiline: true,
            rows: 2,
            toolbar: 'full',
            onInput: (v) => store.input(`${t.id}.c`, () => (t.content = v)),
            addon:
              s.texts.length > 1
                ? h('button', { class: 'icon-btn danger', type: 'button', title: 'Удалить блок', onclick: () => store.change(() => s.texts.splice(i, 1)) }, icon('trash', 15))
                : undefined,
          }),
        ),
      ),
      addButton('Текстовый блок', () => store.change(() => s.texts.push(newText(''))), { small: true, disabled: s.texts.length >= LIMITS.sectionTexts }),
    ),
    group(
      'Справа',
      segmented(
        [
          { value: 'thumbnail', label: 'Миниатюра', icon: 'image' },
          { value: 'button', label: 'Кнопка', icon: 'mouse' },
        ],
        acc.type,
        (v) => {
          store.input(`${s.id}.acc`, () => {
            s.accessory = v === 'button' ? newButton('secondary') : { type: 'thumbnail', url: '', description: '', spoiler: false };
          });
          renderAcc();
        },
        'Аксессуар секции',
      ),
      accBox,
    ),
  ];
}

function childFields(c: DV2Child): HTMLElement[] {
  const d = store.draft;
  switch (c.type) {
    case 'text':
      return [textField({ value: c.content, label: 'Текст', multiline: true, rows: 3, toolbar: 'full', onInput: (v) => store.input(`${c.id}.c`, () => (c.content = v)) })];
    case 'section':
      return sectionFields(c);
    case 'gallery':
      return [
        h(
          'div',
          { class: 'nested-list' },
          c.items.map((it, i) =>
            itemCard({
              id: it.id,
              level: 2,
              icon: 'image',
              title: `Медиа ${i + 1}`,
              subtitle: it.url ? it.url.replace(/^upload:\/\/[^/]+\//, '') : 'нет ссылки',
              defaultCollapsed: c.items.length > 3,
              actions: listActions(c.items, i, (x) => cloneWithNewIds(x)),
              body: () => [
                mediaField('Картинка или видео', it.url, (v) => store.input(`${it.id}.u`, () => (it.url = v)), { accept: 'image/*,video/*' }),
                textField({ label: 'Описание (alt)', value: it.description, max: LIMITS.mediaDescription, onInput: (v) => store.input(`${it.id}.d`, () => (it.description = v)) }),
                switchField('Спойлер', it.spoiler, (v) => store.input(`${it.id}.s`, () => (it.spoiler = v))),
              ],
            }),
          ),
        ),
        h(
          'div',
          { class: 'btn-row' },
          addButton('Медиа', () => store.change(() => c.items.push(newMediaItem())), { small: true, disabled: c.items.length >= LIMITS.galleryItems }),
          addButton(
            'Загрузить несколько',
            async () => {
              const files = await pickFile('image/*,video/*', true);
              const free = LIMITS.galleryItems - c.items.length;
              const urls: string[] = [];
              for (const f of files.slice(0, free)) {
                try {
                  urls.push(await uploadFile(f));
                } catch (err: any) {
                  toast(err.message ?? 'Ошибка загрузки', 'error');
                }
              }
              if (urls.length) {
                store.change(() => {
                  // пустой первый элемент заменяем
                  if (c.items.length === 1 && !c.items[0].url) c.items.splice(0, 1);
                  for (const u of urls) c.items.push(newMediaItem(u));
                });
              }
            },
            { small: true, disabled: c.items.length >= LIMITS.galleryItems },
          ),
        ),
      ];
    case 'file':
      return [
        d.attachments.length
          ? selectField(
              'Вложение',
              c.attachment,
              [{ value: '', label: '- выберите файл -' }, ...d.attachments.map((a) => ({ value: a.name, label: a.name }))],
              (v) => store.input(`${c.id}.a`, () => (c.attachment = v)),
            )
          : emptyHint('Сначала добавьте файл в разделе «Вложения» ниже'),
        switchField('Спойлер', c.spoiler, (v) => store.input(`${c.id}.s`, () => (c.spoiler = v))),
      ];
    case 'separator':
      return [
        switchField('Показывать линию', c.divider, (v) => store.input(`${c.id}.div`, () => (c.divider = v))),
        h(
          'div',
          { class: 'field' },
          h('div', { class: 'field-head' }, h('span', { class: 'field-label' }, 'Отступ')),
          segmented(
            [
              { value: 1, label: 'Маленький' },
              { value: 2, label: 'Большой' },
            ],
            c.spacing,
            (v) => store.input(`${c.id}.sp`, () => (c.spacing = v as 1 | 2)),
            'Отступ',
          ),
        ),
      ];
    case 'row':
      return [];
  }
}

function subtitleOf(c: DV2Child | DV2Top): string {
  switch (c.type) {
    case 'text':
      return c.content.split('\n')[0].slice(0, 60) || 'пусто';
    case 'section':
      return `${c.texts[0]?.content.split('\n')[0].slice(0, 40) ?? ''} · ${c.accessory.type === 'button' ? 'кнопка' : 'миниатюра'}`;
    case 'gallery':
      return `${c.items.length} медиа`;
    case 'file':
      return c.attachment || 'файл не выбран';
    case 'separator':
      return `${c.divider ? 'линия' : 'без линии'}, ${c.spacing === 2 ? 'большой' : 'маленький'} отступ`;
    case 'container':
      return `${c.children.length} эл.`;
    case 'row':
      return `${c.items.length} эл.`;
  }
}

function childCard(c: DV2Child, list: DV2Child[] | DV2Top[], i: number, level: 0 | 1): HTMLElement {
  const actions = listActions(list as any[], i, (x) => cloneWithNewIds(x));
  if (c.type === 'row') return rowEditor(c, NAMES.row.title, actions, level);
  return itemCard({
    id: c.id,
    level,
    icon: NAMES[c.type].icon,
    title: NAMES[c.type].title,
    subtitle: subtitleOf(c),
    actions,
    body: () => childFields(c),
  });
}

function containerCard(c: DContainer, i: number): HTMLElement {
  const d = store.draft;
  return itemCard({
    id: c.id,
    icon: 'box',
    accent: c.accent === null ? null : hexColor(c.accent),
    title: 'Контейнер',
    subtitle: subtitleOf(c),
    actions: listActions(d.v2, i, (x) => cloneWithNewIds(x)),
    body: () => [
      h(
        'div',
        { class: 'row-fields' },
        colorField('Цвет полосы', c.accent, (v) => store.input(`${c.id}.accent`, () => (c.accent = v))),
        h('div', { class: 'field switch-cell' }, switchField('Спойлер', c.spoiler, (v) => store.input(`${c.id}.sp`, () => (c.spoiler = v)))),
      ),
      c.children.length
        ? h('div', { class: 'nested-list v2-children' }, c.children.map((ch, j) => childCard(ch, c.children, j, 1)))
        : emptyHint('Пустой контейнер - добавьте содержимое'),
      addButton('Внутрь контейнера', addItems((x) => store.change(() => c.children.push(x as DV2Child)), false), {
        small: true,
        disabled: c.children.length >= LIMITS.containerChildren,
      }),
    ],
  });
}

export function v2Editor(): HTMLElement[] {
  const d = store.draft;
  const total = countV2Components(d.v2);
  const text = v2TextLength(d.v2);
  return [
    sectionPanel({
      id: 'sec-v2',
      title: 'Компоненты V2',
      icon: 'box',
      badge: `${total}/${LIMITS.v2Components} комп. · ${text}/${LIMITS.v2TextTotal} симв.`,
      body: () => [
        d.v2.length
          ? h('div', { class: 'list' }, d.v2.map((c, i) => (c.type === 'container' ? containerCard(c, i) : childCard(c, d.v2, i, 0))))
          : emptyHint('Components V2 - новый формат Discord: вместо content и эмбедов сообщение собирается из блоков'),
        addButton('Компонент', addItems((x) => store.change(() => d.v2.push(x)), true), { disabled: total >= LIMITS.v2Components }),
      ],
    }),
  ];
}
